import { zodResolver } from "@hookform/resolvers/zod";
import { TFunction } from "i18next";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { useResetPassword } from "../../../api/auth/forgetPassword/useResetPassword";
import Ic_wow from "../../../assets/images/icons/blueSuccess.svg";
import {
  Modal,
  ConfirmationPopup,
  GeneralButton,
  GeneralInput,
} from "../../../components";
import { resetUserDetails } from "../../../redux/reducers/registrationSlice";
import { resetUserState } from "../../../redux/reducers/userSlice";
import { persistor } from "../../../redux/store";
import { useToast } from "../../../Services/ToastContext";
import AuthLayout from "../../../Layout/AuthLayout";
import { useAppDispatch, useAppSelector } from "../../../hooks";

interface PasswordFormInputs {
  Password: string;
}

interface PasswordValidationState {
  isUpperCasePresent: boolean;
  isNumberPresent: boolean;
  isPasswordLongEnough: boolean;
}

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { showToast } = useToast();

  const { Id, VerificationCode } = useAppSelector(
    (state) => state.forgetPassword,
  );

  const createPasswordSchema = (t: TFunction) => {
    return z.object({
      Password: z
        .string()
        .min(8, t("password_min_length"))
        .regex(/[A-Z]/, t("password_uppercase_required"))
        .regex(/\d/, t("password_number_required")),
    });
  };

  const passwordStepSchema = useMemo(() => createPasswordSchema(t), [t]);

  const methods = useForm<PasswordFormInputs>({
    defaultValues: {
      Password: "",
    },
    resolver: zodResolver(passwordStepSchema),
    mode: "onChange",
  });

  const {
    handleSubmit,
    formState: { isSubmitted },
    watch,
    trigger,
  } = methods;

  const Password = watch("Password", "");

  // Password validation state
  const isUpperCasePresent = /[A-Z]/.test(Password);
  const isNumberPresent = /\d/.test(Password);
  const isPasswordLongEnough = Password.length >= 8;

  const getSectorColor = (isMet: boolean, totalMet: number): string => {
    if (!isMet) return "bg-primary-bg";

    if (totalMet === 1) return "bg-error";
    if (totalMet === 2) return "bg-orange";
    if (totalMet === 3) return "bg-successGreen";

    return "bg-primary-bg";
  };

  const passwordConditions = [
    isUpperCasePresent,
    isNumberPresent,
    isPasswordLongEnough,
  ];
  const { mutate, isPending } = useResetPassword();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    if (isSubmitted) {
      trigger();
    }
  }, [t, trigger, isSubmitted]);

  useEffect(() => {
    if (!Id || !VerificationCode) {
      navigate("/forgot-password", { replace: true });
    }
  }, [Id, VerificationCode, navigate]);

  const onSubmit: SubmitHandler<PasswordFormInputs> = useCallback(
    (data) => {
      if (!Id || !VerificationCode) {
        showToast(t("missing_verification_info"), "error");
        navigate("/forgot-password");
        return;
      }

      const requestData = {
        Id: Id,
        VerificationCode: VerificationCode,
        Password: data.Password,
      };

      mutate(requestData, {
        onSuccess: () => {
          setIsModalOpen(true);
        },
        onError: (error: any) => {
          showToast(
            error.response?.data?.ErrorMessage || error.message,
            "error",
          );
        },
      });
    },
    [Id, VerificationCode, mutate, navigate, showToast, t],
  );

  const handleConfirmGoBack = useCallback(() => {
    setShowConfirmation(false);

    dispatch(resetUserDetails());
    dispatch(resetUserState());
    persistor.purge();
    navigate("/forgot-password", { replace: true });
  }, [navigate]);

  const handleCancelGoBack = useCallback(() => {
    setShowConfirmation(false);
  }, []);

  // Prevent navigation and show confirmation popup
  useEffect(() => {
    window.history.pushState(null, "", window.location.href);

    const handlePopState = (event: PopStateEvent) => {
      setShowConfirmation(true);
      window.history.pushState(null, "", window.location.href);
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const getFeedbackTextColor = (isMet: boolean, currentPassword: string) => {
    if (!currentPassword) {
      return "text-body-text"; // Initial gray before typing
    }
    return isMet ? "text-successGreen" : "text-error";
  };
  const validatePasswordChars: PasswordValidationState = {
    isUpperCasePresent: /[A-Z]/.test(Password),
    isNumberPresent: /\d/.test(Password),
    isPasswordLongEnough: Password.length >= 8,
  };

  // Count how many conditions are met in total
  const criteriaMetCount = Object.values(validatePasswordChars).filter(
    Boolean,
  ).length;

  return (
    <FormProvider {...methods}>
      <AuthLayout
        title={t("create_new_password")}
        description={`${t("create_your_password_msg")}`}
        lottieImage={null}
      >
        <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-4">
            <GeneralInput
              type="password"
              name="Password"
              label={t("password")}
              placeholder={t("password")}
              required
              showErrorMessage={false}
            />
          </div>

          {/* Sectors for password validation feedback */}
          <div className="password-feedback-container mt-2 flex w-full gap-1">
            {passwordConditions.map((conditionMet, index) => (
              <div
                key={index}
                className={`h-1 w-1/3 rounded ${getSectorColor(
                  conditionMet,
                  criteriaMetCount,
                )}`}
              ></div>
            ))}
          </div>

          {/* Password Requirements */}
          <ul className="my-4 space-y-1 text-sm">
            <li
              className={getFeedbackTextColor(
                validatePasswordChars.isUpperCasePresent,
                Password,
              )}
            >
              {t("at_least_one_uppercase_letter")}
            </li>
            <li
              className={getFeedbackTextColor(
                validatePasswordChars.isNumberPresent,
                Password,
              )}
            >
              {t("must_contain_numbers")}
            </li>
            <li
              className={getFeedbackTextColor(
                validatePasswordChars.isPasswordLongEnough,
                Password,
              )}
            >
              {t("at_least_8_characters")}
            </li>
          </ul>

          <GeneralButton
            type="submit"
            title={t("create_password")}
            color="blue"
            block
            disabled={!Password || isPending}
          />
        </form>
      </AuthLayout>

      <Modal
        isOpen={isModalOpen}
        onClose={() => navigate("/login", { replace: true })}
      >
        <img
          src={Ic_wow}
          alt="Wow icon"
          className="w-24 max-w-32 object-scale-down mx-auto"
        />
        <h2 className="mb-4 text-center text-2xl font-bold">
          {t("your_account_is_secure_now")}
        </h2>
        <p className="mb-4 text-center text-body-text">
          {t("your_password_has_been_created_successfully")}
        </p>
        <div className="flex">
          <GeneralButton
            title={t("continue")}
            onClick={() => navigate("/login", { replace: true })}
            block={true}
          />
        </div>
      </Modal>

      {showConfirmation && (
        <ConfirmationPopup
          isModalOpen={showConfirmation}
          title={t("all_info_will_lost")}
          description={t("are_you_sure_to_do_action")}
          onClose={handleCancelGoBack}
          onConfirm={handleConfirmGoBack}
          ariaLabel={t("confirm_navigation")}
        />
      )}
    </FormProvider>
  );
};

export default ResetPassword;
